import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MoneyValue } from "@/components/MoneyValue";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { SessionStatCard } from "./SessionStatCard";

interface OpenSessionDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: any;
}

export default function OpenSessionDetailsModal({ open, onOpenChange, session }: OpenSessionDetailsModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["open-session-progress", session?.id],
    queryFn: async () => {
      if (!session?.id) return null;
      const nowIso = new Date().toISOString();

      // Pagamentos (entradas) relacionados à sessão ou à janela desde a abertura
      // Filtrar por data para garantir que apenas transações dentro do período sejam incluídas
      const { data: paysBySession } = await supabase
        .from('transaction_payments')
        .select('id, created_at, confirmed_at, status, valor, net_amount, data_prevista, bank_account_id, payment_method_id, transactions!inner(id, created_at, cash_register_session_id)')
        .eq('transactions.cash_register_session_id', session.id)
        .gte('transactions.created_at', session.opened_at)
        .lte('transactions.created_at', nowIso);

      const { data: paysNoSession } = await supabase
        .from('transaction_payments')
        .select('id, created_at, confirmed_at, status, valor, net_amount, data_prevista, bank_account_id, payment_method_id, transactions!inner(id, created_at, tenant_id)')
        .eq('transactions.tenant_id', session.tenant_id)
        .is('transactions.cash_register_session_id', null)
        .gte('transactions.created_at', session.opened_at)
        .lte('transactions.created_at', nowIso);

      const payments = [...(paysBySession || []), ...(paysNoSession || [])];

      // Despesas (saídas) - filtrar por data para garantir que apenas transações dentro do período sejam incluídas
      const { data: expBySession } = await supabase
        .from('transactions')
        .select('id, created_at, valor_liquido, bank_account_id, tipo')
        .eq('cash_register_session_id', session.id)
        .eq('tipo', 'saida')
        .gte('created_at', session.opened_at)
        .lte('created_at', nowIso);
      const { data: expNoSession } = await supabase
        .from('transactions')
        .select('id, created_at, valor_liquido, bank_account_id, tipo')
        .eq('tenant_id', session.tenant_id)
        .eq('tipo', 'saida')
        .is('cash_register_session_id', null)
        .gte('created_at', session.opened_at)
        .lte('created_at', nowIso);
      const expenses = [...(expBySession || []), ...(expNoSession || [])];

      // Snapshots da abertura por conta
      const { data: openings } = await supabase
        .from('cash_register_breakdown')
        .select('bank_account_id, declared_amount')
        .eq('session_id', session.id)
        .eq('phase', 'opening');

      const openingByAcc = new Map<string, number>();
      (openings || []).forEach((o: any) => openingByAcc.set(o.bank_account_id, Number(o.declared_amount) || 0));

      const closingDate = new Date();

      // Entradas liquidadas por conta (usa net_amount quando disponível)
      const incomeLiquidatedByAcc = new Map<string, number>();
      const incomeFutureByAcc = new Map<string, number>();
      const incomePendingByAcc = new Map<string, number>();
      (payments || []).forEach((p: any) => {
        const acc = p.bank_account_id as string | null;
        if (!acc) return;
        const status = (p.status || 'pendente') as string;
        if (status === 'pendente') {
          incomePendingByAcc.set(acc, (incomePendingByAcc.get(acc) || 0) + (Number(p.valor) || 0));
          return;
        }
        if (status !== 'confirmado') return;
        const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
        const isLiquidated = !dataPrevista || dataPrevista <= closingDate;
        const val = Number(p.net_amount ?? p.valor) || 0;
        if (isLiquidated) {
          incomeLiquidatedByAcc.set(acc, (incomeLiquidatedByAcc.get(acc) || 0) + val);
        } else {
          incomeFutureByAcc.set(acc, (incomeFutureByAcc.get(acc) || 0) + val);
        }
      });

      const expenseByAcc = new Map<string, number>();
      (expenses || []).forEach((t: any) => {
        const acc = t.bank_account_id as string | null;
        if (!acc) return;
        expenseByAcc.set(acc, (expenseByAcc.get(acc) || 0) + (Number(t.valor_liquido) || 0));
      });

      // Contas envolvidas
      const accIds = Array.from(new Set([
        ...Array.from(openingByAcc.keys()),
        ...payments.map((p:any)=>p.bank_account_id).filter(Boolean),
        ...expenses.map((e:any)=>e.bank_account_id).filter(Boolean),
      ]));

      const { data: accs } = await supabase
        .from('bank_accounts')
        .select('id, nome')
        .in('id', accIds.length ? accIds : ['00000000-0000-0000-0000-000000000000']);
      const nameById = new Map<string, string>();
      (accs || []).forEach((a:any)=> nameById.set(a.id, a.nome));

      // Enriquecer nomes de método e cliente/produtos para listas
      const pmIds = Array.from(new Set((payments || []).map((p:any)=>p.payment_method_id).filter(Boolean)));
      const { data: pms } = pmIds.length
        ? await supabase.from('payment_methods').select('id, nome').in('id', pmIds)
        : { data: [] as any[] } as any;
      const pmNameById = new Map<string, string>();
      (pms || []).forEach((m:any)=> pmNameById.set(m.id, m.nome));

      const txIds = Array.from(new Set((payments || []).map((p:any)=>p.transaction_id).filter(Boolean)));
      const clientIds = Array.from(new Set((payments || []).map((p:any)=>p.transactions?.client_id).filter(Boolean)));
      const [{ data: clientsData }, { data: itemsData }] = await Promise.all([
        clientIds.length ? supabase.from('clients').select('id, nome').in('id', clientIds) : Promise.resolve({ data: [] as any[] } as any),
        txIds.length ? supabase.from('transaction_items').select('transaction_id, products!inner(nome)').in('transaction_id', txIds) : Promise.resolve({ data: [] as any[] } as any),
      ]);
      const clientNameById = new Map<string, string>();
      (clientsData || []).forEach((c:any)=> clientNameById.set(c.id, c.nome));
      const productNamesByTx = new Map<string, string>();
      (itemsData || []).forEach((it:any)=> {
        const prev = productNamesByTx.get(it.transaction_id) || '';
        const next = it.products?.nome ? (prev ? `${prev}, ${it.products.nome}` : it.products.nome) : prev;
        productNamesByTx.set(it.transaction_id, next);
      });

      const rows = accIds.map((id) => {
        const opening = openingByAcc.get(id) || 0;
        const liquid = incomeLiquidatedByAcc.get(id) || 0;
        const future = incomeFutureByAcc.get(id) || 0;
        const pending = incomePendingByAcc.get(id) || 0;
        const exp = expenseByAcc.get(id) || 0;
        const expected = Math.max(0, opening + liquid - exp);
        return {
          id,
          name: nameById.get(id) || 'Desconhecido',
          opening,
          liquid,
          future,
          pending,
          expenses: exp,
          expected,
        };
      });

      const totals = rows.reduce((s, r) => ({
        opening: s.opening + r.opening,
        liquid: s.liquid + r.liquid,
        future: s.future + r.future,
        pending: s.pending + r.pending,
        expenses: s.expenses + r.expenses,
        expected: s.expected + r.expected,
      }), { opening:0, liquid:0, future:0, pending:0, expenses:0, expected:0 });

      // Construir linhas detalhadas (entradas/saídas) com nomes
      const incomeRows = (payments || []).map((p:any) => ({
        id: p.id,
        created_at: p.confirmed_at || p.created_at,
        bank_account_name: p.bank_account_id ? (nameById.get(p.bank_account_id) || '-') : '-',
        payment_method_name: p.payment_method_id ? (pmNameById.get(p.payment_method_id) || '-') : '-',
        data_prevista: p.data_prevista ?? null,
        status: p.status as string,
        valor: Number(p.valor) || 0,
        net_amount: p.net_amount ?? undefined,
        client_name: p.transactions?.client_id ? (clientNameById.get(p.transactions.client_id) || '-') : undefined,
        product_names: p.transaction_id ? (productNamesByTx.get(p.transaction_id) || undefined) : undefined,
      }));

      const expenseRows = (expenses || []).map((t:any) => ({
        id: t.id,
        created_at: t.created_at,
        bank_account_name: t.bank_account_id ? (nameById.get(t.bank_account_id) || '-') : '-',
        payment_method_name: '-',
        valor_liquido: Number(t.valor_liquido) || 0,
      }));

      const summaryLiquidated = incomeRows.filter((p) => {
        if ((p.status || 'pendente') !== 'confirmado') return false;
        const d = p.data_prevista ? new Date(p.data_prevista) : null;
        return !d || d <= closingDate;
      });
      const summaryFuture = incomeRows.filter((p) => {
        if ((p.status || 'pendente') !== 'confirmado') return false;
        const d = p.data_prevista ? new Date(p.data_prevista) : null;
        return !!d && d > closingDate;
      });
      const summaryPending = incomeRows.filter((p) => (p.status || 'pendente') === 'pendente');
      const summaryExpenses = expenseRows;

      const sum = (arr: any[], pick: (x:any)=>number) => arr.reduce((s,x)=> s + (pick(x) || 0), 0);
      const summaryLiquidatedTotal = sum(summaryLiquidated, (p:any)=> Number(p.net_amount ?? p.valor) || 0);
      const summaryFutureTotal = sum(summaryFuture, (p:any)=> Number(p.net_amount ?? p.valor) || 0);
      const summaryPendingTotal = sum(summaryPending, (p:any)=> Number(p.valor) || 0);
      const summaryExpensesTotal = sum(summaryExpenses, (t:any)=> Number(t.valor_liquido) || 0);

      return {
        rows,
        totals,
        summaryLiquidated,
        summaryFuture,
        summaryPending,
        summaryExpenses,
        summaryLiquidatedTotal,
        summaryFutureTotal,
        summaryPendingTotal,
        summaryExpensesTotal,
      };
    },
    enabled: !!session?.id && open,
    refetchOnWindowFocus: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col min-h-0 overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b bg-background sticky top-0 z-10">
          <DialogTitle>Sessão de Caixa Aberta</DialogTitle>
        </DialogHeader>

        {!session ? null : (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Operador</p>
                <p className="font-medium">{session.profiles?.nome_completo || '-'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Abertura</p>
                <p className="font-medium">{format(new Date(session.opened_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Saldo de Abertura</p>
                <MoneyValue asBlock value={Number(session.opening_balance || 0)} className="justify-start" />
              </div>
            </div>

            {isLoading || !data ? (
              <div className="py-8 text-center text-muted-foreground">Carregando...</div>
            ) : (
              <>
                <div>
                  <h4 className="text-sm font-medium mb-2">Por Conta</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Conta</TableHead>
                        <TableHead className="text-right">Abertura</TableHead>
                        <TableHead className="text-right">Entradas liquidadas</TableHead>
                        <TableHead className="text-right">Saídas</TableHead>
                        <TableHead className="text-right">Esperado agora</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.rows.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.name}</TableCell>
                          <TableCell className="text-right"><MoneyValue value={r.opening} /></TableCell>
                          <TableCell className="text-right"><MoneyValue value={r.liquid} /></TableCell>
                          <TableCell className="text-right"><MoneyValue value={r.expenses} /></TableCell>
                          <TableCell className="text-right"><MoneyValue value={r.expected} /></TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-bold">Totais</TableCell>
                        <TableCell className="text-right font-bold"><MoneyValue value={data.totals.opening} /></TableCell>
                        <TableCell className="text-right font-bold"><MoneyValue value={data.totals.liquid} /></TableCell>
                        <TableCell className="text-right font-bold"><MoneyValue value={data.totals.expenses} /></TableCell>
                        <TableCell className="text-right font-bold"><MoneyValue value={data.totals.expected} /></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Boxes iguais ao resumo do Fechar Caixa */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Liquidados */}
                  <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Pagamentos Liquidados</h4>
                        <p className="text-xs text-muted-foreground">Entram no saldo agora (todas as contas)</p>
                      </div>
                      <div className="font-bold text-green-700">
                        <MoneyValue value={data.summaryLiquidatedTotal} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {data.summaryLiquidated.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Sem pagamentos liquidados.</div>
                      ) : (
                        data.summaryLiquidated.map((p: any, i: number) => (
                          <div key={`sum-liq-${i}`} className="flex flex-wrap items-start justify-between text-xs gap-2">
                            <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                              <div className="font-medium text-foreground">
                                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                              </div>
                              {p.payment_method_name || "-"}
                              {p.bank_account_name && (
                                <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                              )}
                              {p.data_prevista && (
                                <span className="ml-1 text-[10px]">(liquidou em {new Date(p.data_prevista).toLocaleDateString('pt-BR')})</span>
                              )}
                              {(p.client_name || p.product_names) && (
                                <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0">
                                  {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                                </div>
                              )}
                            </div>
                            <div className="font-medium shrink-0 text-right">
                              <MoneyValue value={Number(p.net_amount ?? p.valor) || 0} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Despesas */}
                  <div className="rounded-lg border-2 border-red-200 bg-red-50/50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Despesas</h4>
                        <p className="text-xs text-muted-foreground">Descontam no dia (sempre instantâneas)</p>
                      </div>
                      <div className="font-bold text-red-700">
                        <MoneyValue value={data.summaryExpensesTotal} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {data.summaryExpenses.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Sem despesas.</div>
                      ) : (
                        data.summaryExpenses.map((t: any, i: number) => (
                          <div key={`sum-exp-${i}`} className="flex flex-wrap items-start justify-between text-xs gap-2">
                            <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                              <div className="font-medium text-foreground">
                                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(t.created_at))}
                              </div>
                              {t.payment_method_name || "-"}
                              {t.bank_account_name && (
                                <span className="ml-1 text-[10px]">({t.bank_account_name})</span>
                              )}
                            </div>
                            <div className="font-medium shrink-0 text-right">
                              <MoneyValue value={Number(t.valor_liquido) || 0} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Abaixo: Futuros e Pendentes */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Futuros */}
                  <div className="rounded-lg border-2 border-orange-200 bg-orange-50/40 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Pagamentos Futuros</h4>
                        <p className="text-xs text-muted-foreground">Não entram no saldo de hoje</p>
                      </div>
                      <div className="font-bold text-orange-700">
                        <MoneyValue value={data.summaryFutureTotal} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {data.summaryFuture.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Sem pagamentos futuros.</div>
                      ) : (
                        data.summaryFuture.map((p: any, i: number) => (
                          <div key={`sum-fut-${i}`} className="flex flex-wrap items-start justify-between text-xs gap-2">
                            <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                              <div className="font-medium text-foreground">
                                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                              </div>
                              {p.payment_method_name || "-"}
                              {p.bank_account_name && (
                                <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                              )}
                              {p.data_prevista && (
                                <span className="ml-1 text-[10px]">(previsto para {new Date(p.data_prevista).toLocaleDateString('pt-BR')})</span>
                              )}
                            </div>
                            <div className="font-medium shrink-0 text-right">
                              <MoneyValue value={Number(p.net_amount ?? p.valor) || 0} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Pendentes */}
                  <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50/40 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Pagamentos Pendentes</h4>
                        <p className="text-xs text-muted-foreground">Aguardando confirmação</p>
                      </div>
                      <div className="font-bold text-yellow-700">
                        <MoneyValue value={data.summaryPendingTotal} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {data.summaryPending.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Sem pagamentos pendentes.</div>
                      ) : (
                        data.summaryPending.map((p: any, i: number) => (
                          <div key={`sum-pend-${i}`} className="flex flex-wrap items-start justify-between text-xs gap-2">
                            <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                              <div className="font-medium text-foreground">
                                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                              </div>
                              {p.payment_method_name || "-"}
                              {p.bank_account_name && (
                                <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                              )}
                            </div>
                            <div className="font-medium shrink-0 text-right">
                              <MoneyValue value={Number(p.valor) || 0} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

              </>
            )}
          </div>
        )}

        <div className="mt-auto px-6 py-3 border-t bg-background flex justify-end gap-2 sticky bottom-0 z-10">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
